import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService } from 'src/app/shared/services/base.service';
import { ResultOfT } from 'src/app/shared/models/response/result';
import { WebStorageService } from 'src/app/shared/services/web-storage.service';

export interface ImportBatchDto {
  importBatchId: number;
  fileName: string;
  entityType: string;
  sourceSystem: string;
  totalRows: number;
  successRows: number;
  failedRows: number;
  skippedRows: number;
  status: string;
  errorNote: string | null;
  dateTimeCreated: string;
  dateTimeCompleted: string | null;
}

export interface ImportRowLogDto {
  rowNumber: number;
  entityType: string;
  externalKey: string | null;
  action: string;
  message: string | null;
  dateTimeCreated: string;
}

export interface ImportBatchDetailDto extends ImportBatchDto {
  rowLogs: ImportRowLogDto[];
}

export interface ImportAcceptedDto {
  importBatchId: number;
  fileName: string;
  entityType: string;
  status: string;
  message: string;
}

export interface MappingProfileDto {
  mappingProfileId: number;
  entityType: string;
  profileName: string;
  version: number;
  isActive: boolean;
  hasHeaderRow: boolean;
  delimiter: string;
  fieldMappings: FieldMappingDto[];
}

export interface FieldMappingDto {
  fieldMappingId: number;
  mappingProfileId: number;
  sourceColumnName: string;
  targetColumnName: string;
  targetEntity: string;
  isRequired: boolean;
  isKey: boolean;
  transformType: string;
  defaultValue: string | null;
  isActive: boolean;
}

export interface TargetFieldCatalogDto {
  targetFieldCatalogId: number;
  entityType: string;
  targetColumnName: string;
  friendlyLabel: string;
  dataType: string;
  isRequired: boolean;
  isKey: boolean;
}

export interface IntegrationConfigDto {
  configId: number;
  duplicateStrategy: string;
  importFolderPath: string | null;
  exportFolderPath: string | null;
  exportScheduleCron: string | null;
  archiveRetentionDays: number;
  allowManualEntry: boolean;
  dateTimeCreated: string;
  dateTimeLastModified: string;
}

@Injectable({ providedIn: 'root' })
export class CsvImportService extends BaseService {
  constructor(httpService: HttpClient, webStorageService: WebStorageService) {
    super(httpService, webStorageService);
  }

  uploadCsv(file: File, entityType: string): Observable<ResultOfT<ImportAcceptedDto>> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.post<ResultOfT<ImportAcceptedDto>>(`CsvImport/upload?entityType=${entityType}`, formData);
  }

  /** Upload file + inline field mapping in one call */
  uploadCsvWithMapping(file: File, entityType: string, fieldMappings: FieldMappingDto[]): Observable<ResultOfT<ImportAcceptedDto>> {
    const formData = new FormData();
    const timestamp = new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 15); // yyyyMMdd_HHmmss
    const dotIndex = file.name.lastIndexOf('.');
    const baseName = dotIndex !== -1 ? file.name.slice(0, dotIndex) : file.name;
    const extension = dotIndex !== -1 ? file.name.slice(dotIndex) : '';
    const timestampedFileName = `${baseName}_${timestamp}${extension}`;
    formData.append('file', file, timestampedFileName);
    formData.append('fieldMappings', JSON.stringify(fieldMappings));
    return this.post<ResultOfT<ImportAcceptedDto>>(`CsvImport/upload?entityType=${entityType}`, formData);
  }

  getBatchStatus(batchId: number): Observable<ResultOfT<ImportBatchDto>> {
    return this.get<ResultOfT<ImportBatchDto>>(`CsvImport/${batchId}/status`);
  }

  getBatchDetail(batchId: number): Observable<ResultOfT<ImportBatchDetailDto>> {
    return this.get<ResultOfT<ImportBatchDetailDto>>(`CsvImport/${batchId}/detail`);
  }

  getHistory(from?: string, to?: string, page = 1, pageSize = 20): Observable<ResultOfT<ImportBatchDto[]>> {
    let url = `CsvImport/history?page=${page}&pageSize=${pageSize}`;
    if (from) url += `&from=${from}`;
    if (to) url += `&to=${to}`;
    return this.get<ResultOfT<ImportBatchDto[]>>(url);
  }

  getMappingProfiles(): Observable<ResultOfT<MappingProfileDto[]>> {
    return this.get<ResultOfT<MappingProfileDto[]>>('IntegrationMapping/profiles');
  }

  getTargetFields(entityType: string): Observable<TargetFieldCatalogDto[]> {
    return this.get<TargetFieldCatalogDto[]>(`IntegrationMapping/catalog/${entityType}`);
  }

  saveMappingProfile(profile: MappingProfileDto): Observable<ResultOfT<MappingProfileDto>> {
    return this.post<ResultOfT<MappingProfileDto>>('IntegrationMapping/profiles', profile);
  }

  getIntegrationConfig(): Observable<IntegrationConfigDto | null> {
    return this.get<IntegrationConfigDto | null>('IntegrationConfig');
  }

  saveIntegrationConfig(dto: IntegrationConfigDto): Observable<IntegrationConfigDto> {
    return this.post<IntegrationConfigDto>('IntegrationConfig', dto);
  }

  /** Downloads the original CSV file for a given batch as a Blob. */
  downloadFile(batchId: number): Observable<Blob> {
    return this.getBlob(`CsvImport/${batchId}/download`);
  }

  /**
   * Fetches the TargetFieldCatalog for the given entity type, builds a two-row
   * CSV (header + sample row) and triggers a browser download.
   */
  downloadTemplate(entityType: string): Observable<void> {
    return new Observable(observer => {
      this.getTargetFields(entityType).subscribe({
        next: (fields: TargetFieldCatalogDto[]) => {
          const billing  = new Set(['crm_addressline1','crm_addressline2','crm_city','crm_state','zipcode']);
          const delivery = new Set(['crm_deliveryaddressline1','crm_deliveryaddressline2','crm_deliveryaddresscity','crm_deliveryaddressstate','crm_deliveryaddresszipcode']);
          const addrGroup = (col: string): number => {
            const c = col.toLowerCase();
            if (billing.has(c))  return 1;
            if (delivery.has(c)) return 2;
            return 0;
          };
          const sorted = [...fields].sort((a, b) => {
            if (a.isRequired && !b.isRequired) return -1;
            if (!a.isRequired && b.isRequired) return 1;
            return addrGroup(a.targetColumnName) - addrGroup(b.targetColumnName);
          });

          const headers = sorted.map(f => f.friendlyLabel);
          const sampleRow = sorted.map(f => CsvImportService.sampleValue(f));

          const csvContent = [
            headers.join(','),
            sampleRow.map(v => `"${v}"`).join(',')
          ].join('\r\n');

          const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href     = url;
          link.download = `${entityType}_Import_Template.csv`;
          link.click();
          URL.revokeObjectURL(url);

          observer.next();
          observer.complete();
        },
        error: err => observer.error(err)
      });
    });
  }

  private static sampleValue(field: TargetFieldCatalogDto): string {
    const col = (field.targetColumnName ?? '').toLowerCase();
    const dt  = (field.dataType ?? '').toLowerCase();
    if (col.includes('country'))                              return 'United States';
    if (dt.includes('bool'))                                  return 'true';
    if (dt.includes('int') || dt.includes('number'))          return '1';
    if (dt.includes('date'))                                  return '01/15/2025';
    return `Sample_${field.friendlyLabel.replace(/\s+/g, '_')}`;
  }

  /**
   * Strips the appended timestamp suffix (_yyyyMMddHHmmss) from a blob file name
   * so that the original user-facing name is shown in the UI.
   */
  static getOriginalFileName(fileName: string): string {
    if (!fileName) return fileName;
    const dotIndex = fileName.lastIndexOf('.');
    const baseName  = dotIndex >= 0 ? fileName.slice(0, dotIndex)  : fileName;
    const extension = dotIndex >= 0 ? fileName.slice(dotIndex)     : '';
    // Match trailing _yyyyMMddHHmmss — 16 chars: underscore + 15 digits
    if (baseName.length > 16 && baseName[baseName.length - 16] === '_' &&
        /^\d{15}$/.test(baseName.slice(-15))) {
      return baseName.slice(0, -16) + extension;
    }
    return fileName;
  }
}
